/**
 * Иерархический выбор «Вся компания → кластер → хозяйство» — общая часть.
 *
 * Вынесено из пилюли области в шапке (`DashboardScopeSelector`, удалена в
 * AK-895; UAT founder 08.08: «dropdown — сделать по аналогии с тем, что
 * приложил»), потому что появился ВТОРОЙ потребитель — фильтр хозяйства на
 * экране «Аналитика прошлых лет». Копировать 200 строк дерева ради второго
 * экрана нельзя: разъедутся ровно в тот момент, когда кто-нибудь поправит
 * только одну копию.
 *
 * Здесь — **только представление и чистое дерево**. Семантика выбора у двух
 * потребителей РАЗНАЯ, и это принципиально:
 *
 * * переключатель «Хозяйство ▾» вверху сайдбара (`FarmSwitcher`) меняет
 *   **глобальный scope сессии** (`POST /scope/set` + `dispatchScopeChanged`
 *   через `useScopeSetter`) — это переключение всей системы;
 * * фильтр аналитики выбирает **локально, только для этого экрана**. Иначе
 *   агроном, заглянувший в аналитику соседнего кластера, обнаружил бы, что у
 *   него молча переключилась вся система — карта, дашборд, операции. Такой
 *   побочный эффект и есть «усталостный» баг из CLAUDE.md.
 *
 * Поэтому компонент управляемый: `value` + `onPick`, никаких запросов внутри.
 *
 * ТРЕТИЙ потребитель (24.08, founder про пилюли выбора хозяйства: «этот ux
 * выбора хозяйства надо заменить штатным, который у нас был с правой
 * стороны») — бюджетные справочники сезона. Он принёс сюда две
 * НЕОБЯЗАТЕЛЬНЫЕ настройки, `topOption` и `clustersSelectable`: у экрана есть
 * свой верхний уровень («Шаблон компании», строки без `farm_id`), а выбор
 * кластера ему нечего показать. Настройки аддитивны — у первых двух
 * потребителей поведение прежнее. Оболочку «кнопка + шторка» см. в
 * `ScopeTreePicker`.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Building2, Check, ChevronDown, ChevronRight, MapPin, Network, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { differsAfterRounding } from '@/lib/numberFmt';

export interface ScopeInfo {
  /**
   * Бэк (`/api/v1/scope/available`) отдаёт полный канон типов — в том числе
   * `holding` (имя холдинга для верхнего уровня, см. `topLevelScopeName` в
   * аналитике) и `brigade`. Дерево пикера строит только
   * company/cluster/farm — остальные строки `buildScopeTree` не рисует, но
   * врать типом, что их нет в payload'е, нельзя: узкий union заставлял
   * закрывать глаза кастами на реально приезжающие данные.
   */
  scope_type: 'tenant' | 'holding' | 'company' | 'cluster' | 'farm' | 'brigade';
  scope_id: string | null;
  name: string;
  farms_count?: number | null;
  fields_count?: number | null;
  /**
   * Легаси-мера: вся неархивная земля. Осталась ради онбординга и грантовых
   * строк клиентов, у которых бэк площади вовсе не считает. Дерево читает её
   * только как запасной вариант — см. `scopeArableHa` / `scopeTotalHa`.
   */
  total_area_ha?: number | null;
  /** AK-752: пашня (угодье гоняет севооборот), учётная площадь. */
  arable_ha?: number | null;
  /** AK-752: вся активная земля — пашня + пастбища/сенокосы/лес/несельхоз. */
  total_ha?: number | null;
  parent_cluster_id?: string | null;
}

export interface AvailableScopes {
  current: ScopeInfo;
  available: ScopeInfo[];
}

export type ScopeSelection = Pick<ScopeInfo, 'scope_type' | 'scope_id'>;

export type ScopeTreeGroup = { cluster: ScopeInfo | null; farms: ScopeInfo[] };
export type ScopeTree = {
  company: ScopeInfo | undefined;
  groups: ScopeTreeGroup[];
};

/**
 * Pure tree-builder — extracted for unit testing.
 *
 * Groups farms under their parent cluster; farms whose parent_cluster_id is
 * missing OR points to a cluster the user has no access to fall into an
 * "orphans" group (rendered as «Без кластера»).
 *
 * When `search` is empty:
 * - All groups are kept, INCLUDING empty clusters. This matters for users
 *   whose scope is a single cluster without any farms — otherwise the sheet
 *   renders blank and there's nothing to click.
 *
 * When `search` is non-empty, filters farms by name; keeps a group if either
 * its cluster name matches or it has surviving farms.
 */
export function buildScopeTree(available: readonly ScopeInfo[], search: string): ScopeTree {
  const company = available.find((s) => s.scope_type === 'company');
  const clusters = available.filter((s) => s.scope_type === 'cluster');
  const farms = available.filter((s) => s.scope_type === 'farm');
  const q = search.trim().toLowerCase();

  const clusterIds = new Set(clusters.map((c) => c.scope_id));
  const groups: ScopeTreeGroup[] = clusters.map((c) => ({
    cluster: c,
    farms: farms.filter((f) => f.parent_cluster_id === c.scope_id),
  }));
  const orphans = farms.filter(
    (f) => !f.parent_cluster_id || !clusterIds.has(f.parent_cluster_id),
  );
  if (orphans.length > 0) groups.push({ cluster: null, farms: orphans });

  const filteredGroups = q
    ? groups
        .map((g) => ({
          ...g,
          farms: g.farms.filter((f) => f.name.toLowerCase().includes(q)),
        }))
        .filter(
          (g) => g.farms.length > 0 || (g.cluster && g.cluster.name.toLowerCase().includes(q)),
        )
    : groups;

  return { company, groups: filteredGroups };
}

/**
 * Литерал, которым бэк подписывает company-строку `/scope/available`.
 * Это ПЛЕЙСХОЛДЕР («Вся компания»), а не имя компании — отсюда фильтр ниже.
 */
export const BACKEND_WHOLE_COMPANY_PLACEHOLDER = 'Вся компания';

/**
 * Как зовётся компания целиком — для шапки контрола выбора.
 *
 * Порядок: имя холдинга → имя компании (если бэк прислал настоящее, а не
 * плейсхолдер) → нейтральное «Компания». Жил в экране аналитики (v2-17), но
 * потребитель уже третий, а импортировать хелпер из чужой страницы — тот же
 * путь, которым разъезжаются копии.
 */
export function topLevelScopeName(available: readonly ScopeInfo[]): string {
  const holding = available.find((s) => s.scope_type === 'holding')?.name?.trim();
  if (holding) return holding;
  const company = available.find((s) => s.scope_type === 'company')?.name?.trim();
  if (company && company !== BACKEND_WHOLE_COMPANY_PLACEHOLDER) return company;
  return 'Компания';
}

export function ScopeIcon({
  type,
  className,
}: {
  type: ScopeInfo['scope_type'];
  className?: string;
}) {
  if (type === 'company') return <Building2 className={className} />;
  if (type === 'cluster') return <Network className={className} />;
  return <MapPin className={className} />;
}

export function scopeKey(s: ScopeSelection) {
  return `${s.scope_type}:${s.scope_id ?? ''}`;
}

export function fmtScopeArea(ha: number | null | undefined) {
  if (ha == null) return '';
  return `${Math.round(ha).toLocaleString('ru-RU')} га`;
}

/**
 * Пашня узла — то число, которым дерево ОТЧИТЫВАЕТСЯ (решение founder 25.08).
 *
 * Зачем вообще две функции. До AK-752 узел показывал одну сумму по всей
 * неархивной земле, и «Аргамак · 15 428 га» в навигаторе спорил с 13 958 га на
 * экранах планирования: 105 объектов (15 762 га) выведены из пашни в
 * пастбища/сенокосы/лес. Обе цифры были верны, но человек видит две разные и
 * перестаёт верить обеим.
 *
 * Фолбэк на `total_area_ha` обязателен: у грантовых строк клиентов
 * (`granted: true`) бэк площади не считает вовсе, а у старого кэша ответа новых
 * полей ещё нет. Показать «0 га» там, где раньше было число, — хуже, чем
 * показать легаси-меру.
 */
export function scopeArableHa(s: ScopeInfo): number | null | undefined {
  return s.arable_ha ?? s.total_area_ha;
}

/** Вся активная земля узла — вторая, приглушённая цифра. */
export function scopeTotalHa(s: ScopeInfo): number | null | undefined {
  return s.total_ha ?? s.total_area_ha;
}

/**
 * Есть ли у узла НЕ-пашня, ради которой стоит показывать вторую цифру.
 *
 * Сравниваем округлённые до гектара значения — ровно те, что увидит человек.
 * Иначе на хозяйстве без пастбищ разница в сотые доли (учётная площадь
 * numeric) вылезала бы подписью «13 958 га пашни · 13 958 всего».
 */
export function hasNonArableLand(s: ScopeInfo): boolean {
  const arable = scopeArableHa(s);
  const total = scopeTotalHa(s);
  if (arable == null || total == null) return false;
  // Само правило «сравниваем то, что человек увидит» — общее и живёт в
  // `lib/numberFmt`: тем же меряет карточка площади на экране полей.
  // Направление — местное: `всего < пашни` это дефект данных, и показывать
  // его парой цифр нельзя («13 958 пашни · 13 000 всего» читается как поломка).
  return differsAfterRounding(total, arable) && Math.round(total) > Math.round(arable);
}

/**
 * Гектары в строке дерева.
 *
 * `dual` — узел хозяйства: показываем обе площади, вторую приглушённо.
 * Кластер/компания отчитываются пашней одним числом (решение founder 25.08:
 * в итогах — пашня).
 */
export function ScopeAreaLabel({ scope, dual = false }: { scope: ScopeInfo; dual?: boolean }) {
  const arable = scopeArableHa(scope);
  if (arable == null) return null;
  if (!dual || !hasNonArableLand(scope)) {
    return <span className="text-xs font-normal text-muted-foreground">{fmtScopeArea(arable)}</span>;
  }
  return (
    <span className="text-xs font-normal text-muted-foreground">
      {fmtScopeArea(arable)} пашни{' '}
      <span className="opacity-60">
        · {Math.round(scopeTotalHa(scope) as number).toLocaleString('ru-RU')} всего
      </span>
    </span>
  );
}

/**
 * Найти запись scope по типу и id. Нужен обоим потребителям, чтобы показать
 * на кнопке имя и гектары выбранного объекта, а не голый uuid.
 */
export function findScope(
  available: readonly ScopeInfo[],
  value: ScopeSelection,
): ScopeInfo | undefined {
  return available.find((s) => scopeKey(s) === scopeKey(value));
}

/**
 * Дополнительный пункт верхнего уровня — НЕ scope, а собственная сущность
 * экрана-потребителя.
 *
 * Появился для бюджетных справочников: там верхний уровень — «Шаблон
 * компании» (строки без `farm_id`, из которых копируют в хозяйства), и это
 * НЕ «вся компания» в смысле области видимости. Подменять одно другим
 * нельзя: «вся компания» читается как «все хозяйства сразу», а шаблон —
 * это отдельное место, где цифры лежат и откуда их берут.
 *
 * Поэтому пункт рисуется здесь (один вид у всех строк выбора), но смысл его
 * задаёт потребитель — своим `label`, пояснением и обработчиком.
 */
export interface ScopeTopOption {
  label: string;
  /** Одна строка пояснения под названием — чем этот уровень отличается. */
  hint?: string;
  active: boolean;
  onSelect: () => void;
  /** Иконка слева; по умолчанию — та же, что у company-строки. */
  icon?: ReactNode;
}

export interface ScopeTreeViewProps {
  available: readonly ScopeInfo[];
  /**
   * Что подсвечено «галочкой». `null` — в дереве не выбрано ничего: так
   * бывает, когда выбран собственный верхний уровень экрана (`topOption`),
   * которого среди scope-строк нет.
   */
  value: ScopeSelection | null;
  onPick: (s: ScopeSelection) => void;
  /**
   * Свой пункт над деревом (см. `ScopeTopOption`). Рисуется ВСЕГДА, даже
   * когда company-строки в `/scope/available` нет: у пользователя с доступом
   * на один кластер такой строки не будет, а уровень шаблона от этого никуда
   * не девается.
   */
  topOption?: ScopeTopOption;
  /**
   * Показывать ли строку «Вся компания». Экран, у которого верхний уровень
   * СВОЙ (`topOption`), обязан её гасить — два верхних уровня рядом и есть та
   * самая подмена смысла, ради которой заведён `topOption`.
   */
  showCompanyRow?: boolean;
  /**
   * Можно ли выбрать кластер. Экранам, у которых данные лежат только по
   * хозяйству (бюджетные справочники), выбор кластера нечего показать —
   * клик по строке кластера сворачивает/разворачивает группу.
   */
  clustersSelectable?: boolean;
  /** Подпись строки верхнего уровня. Глобальный дровер говорит «Вся компания»,
   * аналитика (v2-17) передаёт имя холдинга. */
  companyLabel?: string;
  /**
   * Заголовок шапки дерева. По умолчанию — имя company-строки из
   * `/scope/available` (бэк подписывает её «Вся компания»); аналитика
   * передаёт имя холдинга, чтобы контрол звался именем компании целиком.
   */
  headerName?: string;
  /** Плейсхолдер поиска. */
  searchPlaceholder?: string;
  /** Пусто — что показать, когда доступных объектов нет вовсе. */
  emptyLabel?: string;
}

/**
 * Дерево с поиском: «Вся компания» → кластеры → хозяйства, гектары справа.
 *
 * Поиск и свёрнутость групп — локальное состояние представления, наружу их
 * тянуть незачем.
 */
export function ScopeTreeView({
  available,
  value,
  onPick,
  topOption,
  showCompanyRow = true,
  clustersSelectable = true,
  companyLabel = 'Вся компания',
  headerName,
  searchPlaceholder = 'Поиск хозяйства или кластера',
  emptyLabel = 'Нет доступных хозяйств. Обратитесь к администратору для настройки доступа.',
}: ScopeTreeViewProps) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const tree = useMemo(() => buildScopeTree(available, search), [available, search]);
  const isPicked = (s: ScopeSelection) => !!value && scopeKey(value) === scopeKey(s);

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-border p-4">
        <div className="truncate text-base font-semibold">
          {headerName ?? tree.company?.name ?? 'Компания'}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {topOption && (
          <button
            type="button"
            onClick={topOption.onSelect}
            aria-pressed={topOption.active}
            className={cn(
              'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-accent',
              topOption.active && 'bg-primary/10 text-primary',
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              {topOption.icon ?? <ScopeIcon type="company" className="h-4 w-4 shrink-0" />}
              <span className="min-w-0">
                <span className="block truncate font-medium">{topOption.label}</span>
                {topOption.hint && (
                  <span
                    className={cn(
                      'block text-xs',
                      topOption.active ? 'text-primary/80' : 'text-muted-foreground',
                    )}
                  >
                    {topOption.hint}
                  </span>
                )}
              </span>
            </span>
            {topOption.active && <Check className="h-4 w-4 shrink-0" />}
          </button>
        )}

        {showCompanyRow && tree.company && (
          <button
            type="button"
            onClick={() => onPick(tree.company as ScopeInfo)}
            className={cn(
              'flex w-full items-center justify-between gap-2 px-4 py-2 text-sm transition-colors hover:bg-accent',
              isPicked(tree.company) && 'bg-primary/10 text-primary',
            )}
          >
            <span className="flex items-center gap-2">
              <ScopeIcon type="company" className="h-4 w-4" />
              {companyLabel}
            </span>
            <span className="flex items-center gap-2">
              <ScopeAreaLabel scope={tree.company} />
              {isPicked(tree.company) && <Check className="h-4 w-4" />}
            </span>
          </button>
        )}

        {(topOption || (showCompanyRow && tree.company)) && (
          <div className="mt-2 border-t border-border px-4 py-2 pt-3 text-xs uppercase tracking-wide text-muted-foreground">
            Хозяйства
            {tree.company && ` · пашня ${fmtScopeArea(scopeArableHa(tree.company))}`}
          </div>
        )}

        {tree.groups.map((g) => {
          const key = g.cluster ? `cl:${g.cluster.scope_id}` : 'cl:none';
          const isCollapsed = collapsed[key] && !search;
          const isClusterActive =
            clustersSelectable && !!g.cluster && isPicked(g.cluster);

          return (
            <div key={key} className="border-t border-border/50">
              <div
                className={cn(
                  'flex w-full items-center',
                  isClusterActive && 'bg-primary/10 text-primary',
                )}
              >
                <button
                  type="button"
                  onClick={() => setCollapsed((v) => ({ ...v, [key]: !v[key] }))}
                  aria-label={isCollapsed ? 'Раскрыть' : 'Свернуть'}
                  className="px-2 py-2 transition-colors hover:bg-accent"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Кластер выбирается только там, где у выбора кластера есть
                    // смысл. Иначе строка работает как заголовок группы.
                    if (g.cluster && clustersSelectable) onPick(g.cluster);
                    else setCollapsed((v) => ({ ...v, [key]: !v[key] }));
                  }}
                  className="flex flex-1 items-center justify-between gap-2 py-2 pr-4 text-sm font-medium transition-colors hover:bg-accent"
                >
                  <span>{g.cluster ? g.cluster.name : 'Без кластера'}</span>
                  <span className="flex items-center gap-2">
                    {g.cluster ? (
                      <ScopeAreaLabel scope={g.cluster} />
                    ) : (
                      // «Без кластера» — синтетическая группа, у бэка своей
                      // строки нет. Итог складываем из ПАШНИ хозяйств: иначе
                      // группа отчитывалась бы всей землёй, а её кластерные
                      // соседи — пашней, и суммы в одном столбце значили бы
                      // разное.
                      <span className="text-xs font-normal text-muted-foreground">
                        {fmtScopeArea(g.farms.reduce((a, f) => a + (scopeArableHa(f) ?? 0), 0))}
                      </span>
                    )}
                    {isClusterActive && <Check className="h-4 w-4" />}
                  </span>
                </button>
              </div>

              {!isCollapsed && (
                <div className="bg-secondary/30">
                  {g.farms.map((f) => {
                    const isActive = isPicked(f);
                    return (
                      <button
                        key={f.scope_id}
                        type="button"
                        onClick={() => onPick(f)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 py-2 pl-12 pr-4 text-sm transition-colors hover:bg-accent',
                          isActive && 'bg-primary/10 text-primary',
                        )}
                      >
                        <span>{f.name}</span>
                        <span className="flex items-center gap-2">
                          <ScopeAreaLabel scope={f} dual />
                          {isActive && <Check className="h-4 w-4" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {!(showCompanyRow && tree.company) && tree.groups.length === 0 && (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">{emptyLabel}</div>
        )}
      </div>
    </>
  );
}
